import { useEffect, useRef, useState } from 'react';
import { Volume2, VolumeX } from 'lucide-react';

let audioCtx = null;
const ensureCtx = () => {
  if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  if (audioCtx.state === 'suspended') audioCtx.resume();
  return audioCtx;
};

export function playTick(volume = 0.05) {
  try {
    const ctx = ensureCtx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = 'sine'; o.frequency.value = 880;
    g.gain.value = volume;
    o.connect(g); g.connect(ctx.destination);
    o.start();
    o.frequency.exponentialRampToValueAtTime(1320, ctx.currentTime + 0.06);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.12);
    o.stop(ctx.currentTime + 0.13);
  } catch { /* noop */ }
}

/* Ambient pad: three slow sine partials + subtle LFO, gentle low-pass, super quiet.
   Purpose: premium "listening room" feel that builds trust without pulling focus. */
function createAmbient(ctx) {
  const master = ctx.createGain();
  master.gain.value = 0;
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass'; lp.frequency.value = 1200; lp.Q.value = 0.6;
  lp.connect(master);
  master.connect(ctx.destination);

  const freqs = [174.61 /* F3 */, 261.63 /* C4 */, 329.63 /* E4 */];
  const oscs = freqs.map((f) => {
    const o = ctx.createOscillator();
    o.type = 'sine';
    o.frequency.value = f;
    const g = ctx.createGain();
    g.gain.value = 0.14;
    o.connect(g); g.connect(lp);
    o.start();
    return { o, g };
  });

  // slow LFO on the filter to add movement
  const lfo = ctx.createOscillator();
  const lfoGain = ctx.createGain();
  lfo.type = 'sine'; lfo.frequency.value = 0.07;
  lfoGain.gain.value = 320;
  lfo.connect(lfoGain); lfoGain.connect(lp.frequency);
  lfo.start();

  return {
    fadeIn(target = 0.045, seconds = 2.4) {
      const now = ctx.currentTime;
      master.gain.cancelScheduledValues(now);
      master.gain.setValueAtTime(master.gain.value, now);
      master.gain.linearRampToValueAtTime(target, now + seconds);
    },
    fadeOut(seconds = 0.9) {
      const now = ctx.currentTime;
      master.gain.cancelScheduledValues(now);
      master.gain.setValueAtTime(master.gain.value, now);
      master.gain.linearRampToValueAtTime(0.0001, now + seconds);
    },
    stop() {
      try { oscs.forEach(({ o }) => o.stop()); lfo.stop(); } catch { /* noop */ }
    },
  };
}

export default function SoundToggle() {
  const [on, setOn] = useState(false);
  const ambientRef = useRef(null);

  useEffect(() => {
    if (on) {
      const ctx = ensureCtx();
      if (!ambientRef.current) ambientRef.current = createAmbient(ctx);
      ambientRef.current.fadeIn();
    } else if (ambientRef.current) {
      ambientRef.current.fadeOut();
    }
  }, [on]);

  useEffect(() => {
    const handler = (e) => {
      if (!on) return;
      const t = e.target.closest('a, button, [role="button"]');
      if (t) playTick(0.05);
    };
    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, [on]);

  return (
    <button
      data-testid="sound-toggle"
      onClick={() => { setOn(v => !v); if (!on) playTick(0.06); }}
      className="fixed bottom-6 left-6 z-[60] grid h-12 w-12 place-items-center rounded-full border border-ink-100 bg-white/80 backdrop-blur-md text-ink-700 shadow-soft hover:text-cyan transition-colors"
      aria-label={on ? 'Turn sound off' : 'Turn sound on'}
      title={on ? 'Sound on — click to mute' : 'Sound off — click to enable'}
    >
      {on ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
      {on && <span className="absolute -top-1 -right-1 flex h-3 w-3"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan opacity-75" /><span className="relative inline-flex h-3 w-3 rounded-full bg-cyan" /></span>}
    </button>
  );
}
